import type {
  AttributeBoundary,
  GridType,
  MapHeader,
  PaletteBody,
  Range2D,
  ScaleType,
} from '@library/types'
import type { FC } from 'react'
import { useEffect } from 'react'

import { useWebGlContext } from '../context'
import { addRender } from '../helpers'
import { useShader } from './hooks'

type GridTextureProps = {
  mapHeader: MapHeader | undefined
  mapBody: GridType['body']
  palette: PaletteBody
  baseIsoline: number
  step: number
  minorLineWidth: number
  majorLineWidth: number
  majorGridFrequency: number
  scale: ScaleType
  origin: Range2D
  attributeBoundary?: AttributeBoundary
  showIsolines?: boolean
}

export const GridTexture: FC<GridTextureProps> = ({
  mapHeader,
  mapBody,
  palette,
  baseIsoline,
  step,
  minorLineWidth,
  majorLineWidth,
  majorGridFrequency,
  scale,
  origin,
  attributeBoundary,
  showIsolines,
}) => {
  const {
    canvas: { value: containerElement },
    renderQueue: { value: renders },
    update: { value: update, setValue: setUpdate },
    programs,
  } = useWebGlContext()

  const gridRender = useShader(
    containerElement,
    programs,
    scale,
    origin,
    mapHeader,
    mapBody,
    palette,
    attributeBoundary,
    baseIsoline,
    step,
    minorLineWidth,
    majorLineWidth,
    majorGridFrequency,
    showIsolines,
    setUpdate,
  )

  useEffect(() => {
    if (!gridRender) return

    addRender(renders, gridRender)
  }, [gridRender, renders, update])

  return null
}
